import React, { useMemo, useState } from 'react';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import StatusChip from './StatusChip';
import TradeDetailDialog from './TradeDetailDialog';
import { TOLERANCE_QTY, TOLERANCE_VAL } from '../../utils/derivativeReconConstants';
import { fmt, fmtN } from '../../utils/derivativeReconFormat';

function summarize(rows) {
  let matched = 0, breaks = 0, mMil = 0, mCw = 0;
  rows.forEach((r) => {
    if (r.status === 'MATCHED') matched++;
    else if (r.status === 'MISSING_CP') mMil++;
    else if (r.status === 'MISSING_BOR') mCw++;
    else breaks++;
  });
  return { total: rows.length, matched, breaks, missingMil: mMil, missingCw: mCw };
}

function brkColor(b, t) {
  if (b === 0) return '#34d399';
  if (b / t > 0.2) return '#f87171';
  return '#fbbf24';
}

function groupBy(arr, fn) {
  const m = {};
  arr.forEach((r) => { const k = fn(r) || 'Unknown'; if (!m[k]) m[k] = []; m[k].push(r); });
  return m;
}

function toMonth(d) {
  if (!d) return 'Unknown';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? 'Unknown' : dt.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

function mKey(m) {
  if (m === 'Unknown') return '9999-99';
  const d = new Date(m);
  return isNaN(d.getTime()) ? '9999-99' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function BreakBar({ s, bc }) {
  const total = s.breaks + s.missingMil + s.missingCw;
  const pct = s.total ? Math.round((total / s.total) * 100) : 0;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
      <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: bc, borderRadius: 1 } }} />
      <Typography variant="caption" sx={{ minWidth: 32, color: bc, fontWeight: 600 }}>{pct}%</Typography>
    </Stack>
  );
}

function AggGrid({ rows, keyLabel, onRowClick }) {
  const data = useMemo(
    () => rows.map((r) => {
      const s = summarize(r.data);
      const bc = brkColor(s.breaks + s.missingMil + s.missingCw, s.total);
      return { key: r.key, ...s, breakBar: bc, _data: r.data };
    }),
    [rows],
  );

  const columnDefs = useMemo(
    () => [
      { field: 'key', headerName: keyLabel, flex: 1, minWidth: 160 },
      { field: 'total', headerName: 'Total', width: 100, type: 'numericColumn' },
      { field: 'matched', headerName: 'Matched', width: 110, type: 'numericColumn', cellStyle: { color: '#34d399', fontWeight: 600 } },
      { field: 'breaks', headerName: 'Breaks', width: 100, type: 'numericColumn', cellStyle: (p) => p.value > 0 ? { color: '#f87171', fontWeight: 700 } : { color: '#94a3b8' } },
      { field: 'missingMil', headerName: '−Mil', width: 90, type: 'numericColumn', cellStyle: (p) => p.value > 0 ? { color: '#a78bfa', fontWeight: 700 } : { color: '#94a3b8' } },
      { field: 'missingCw', headerName: '−CW', width: 90, type: 'numericColumn', cellStyle: (p) => p.value > 0 ? { color: '#38bdf8', fontWeight: 700 } : { color: '#94a3b8' } },
      { field: 'breakBar', headerName: 'Break Rate', width: 180, cellRenderer: (p) => <BreakBar s={p.data} bc={p.value} /> },
    ],
    [keyLabel],
  );

  return (
    <Box sx={{ height: 540, width: '100%' }}>
      <PamGrid rowData={data} columnDefs={columnDefs} getRowId={(d) => d?.key} onRowClicked={(e) => onRowClick(e.data.key)} defaultColDef={{ sortable: true, resizable: true, filter: true, wrapHeaderText: true, autoHeaderHeight: true }} enableShowToolbar={false} />
    </Box>
  );
}

function Crumbs({ items }) {
  return (
    <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
      {items.map((it, i) => (
        <Button key={i} size="small" variant="outlined" onClick={it.onClick} disabled={!it.onClick} sx={{ fontWeight: it.onClick ? 400 : 700 }}>{it.label}</Button>
      ))}
    </Stack>
  );
}

export default function AggregationView({ posResults }) {
  const [drillEntity, setDrillEntity] = useState(null);
  const [drillMonth, setDrillMonth] = useState(null);
  const [drillBroker, setDrillBroker] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState(null);

  const byEntity = useMemo(() => groupBy(posResults, (r) => r.entity || 'Unknown'), [posResults]);
  const entityRows = useMemo(() => drillEntity ? posResults.filter((r) => (r.entity || 'Unknown') === drillEntity) : [], [posResults, drillEntity]);
  const byMonth = useMemo(() => drillEntity ? groupBy(entityRows, (r) => toMonth(r.milTrade)) : {}, [entityRows, drillEntity]);
  const monthRows = useMemo(() => drillEntity && drillMonth ? entityRows.filter((r) => toMonth(r.milTrade) === drillMonth) : [], [entityRows, drillEntity, drillMonth]);
  const byBroker = useMemo(() => groupBy(monthRows, (r) => r.cpName || 'Unknown'), [monthRows]);

  if (drillBroker) {
    const trades = byBroker[drillBroker] || [];
    const cols = [
      { field: 'id', headerName: 'Trade ID', width: 140 },
      { field: 'entity', headerName: 'Entity', width: 100 },
      { field: 'valDiff', headerName: 'Notional Diff', width: 130, type: 'numericColumn', valueFormatter: (p) => fmt(p.value), cellStyle: (p) => p.value == null ? {} : { color: Math.abs(p.value) > TOLERANCE_VAL ? '#f87171' : '#34d399', fontWeight: 700 } },
      { field: 'qtyDiff', headerName: 'Qty Diff', width: 110, type: 'numericColumn', valueFormatter: (p) => fmtN(p.value), cellStyle: (p) => p.value == null ? {} : { color: Math.abs(p.value) < TOLERANCE_QTY ? '#34d399' : '#f87171', fontWeight: 700 } },
      { field: 'strikeDiff', headerName: 'Strike Diff', width: 110, type: 'numericColumn', valueFormatter: (p) => fmt(p.value) },
      { field: 'cwTrade', headerName: 'CW Trade Dt', width: 120 },
      { field: 'cwSettle', headerName: 'CW Settle Dt', width: 120 },
      { field: 'status', headerName: 'Status', width: 170, cellRenderer: (p) => <StatusChip status={p.value} /> },
    ];
    return (
      <Box>
        <Crumbs items={[
          { label: '← All Entities', onClick: () => { setDrillEntity(null); setDrillMonth(null); setDrillBroker(null); } },
          { label: `← ${drillEntity}`, onClick: () => { setDrillMonth(null); setDrillBroker(null); } },
          { label: `← ${drillMonth}`, onClick: () => setDrillBroker(null) },
          { label: `${drillBroker} — ${trades.length} trade${trades.length !== 1 ? 's' : ''}` },
        ]} />
        <Box sx={{ height: 540, width: '100%' }}>
          <PamGrid rowData={trades} columnDefs={cols} getRowId={(d) => `${d?.id}-${d?.status}`} onRowClicked={(e) => setSelectedTrade(e.data)} defaultColDef={{ sortable: true, resizable: true, filter: true, wrapHeaderText: true, autoHeaderHeight: true }} enableShowToolbar={false} />
        </Box>
        <TradeDetailDialog trade={selectedTrade} type="Positions" onClose={() => setSelectedTrade(null)} />
      </Box>
    );
  }

  if (drillMonth) {
    const brokers = Object.keys(byBroker).sort();
    return (
      <Box>
        <Crumbs items={[
          { label: '← All Entities', onClick: () => { setDrillEntity(null); setDrillMonth(null); setDrillBroker(null); } },
          { label: `← ${drillEntity}`, onClick: () => setDrillMonth(null) },
          { label: `${drillMonth} — by broker` },
        ]} />
        <AggGrid rows={brokers.map((b) => ({ key: b, data: byBroker[b] }))} keyLabel="Broker" onRowClick={setDrillBroker} />
      </Box>
    );
  }

  if (drillEntity) {
    const months = Object.keys(byMonth).sort((a, b) => mKey(b).localeCompare(mKey(a)));
    return (
      <Box>
        <Crumbs items={[
          { label: '← All Entities', onClick: () => { setDrillEntity(null); setDrillMonth(null); setDrillBroker(null); } },
          { label: `${drillEntity} — by month` },
        ]} />
        <AggGrid rows={months.map((m) => ({ key: m, data: byMonth[m] }))} keyLabel="Month" onRowClick={setDrillMonth} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Aggregated by <strong>Entity</strong> — click to drill down
      </Typography>
      <AggGrid
        rows={Object.keys(byEntity).sort().map((e) => ({ key: e, data: byEntity[e] }))}
        keyLabel="Entity"
        onRowClick={setDrillEntity}
      />
    </Box>
  );
}
